import { Box, Hidden, Typography, useTheme } from "@material-ui/core";
import { Rating } from "@material-ui/lab";
import { navigate } from "gatsby";
import moment from "moment";
import React, { useCallback, useState } from "react";
import deepEqual from "react-fast-compare";
import { Helmet } from "react-helmet";
import { useInView } from "react-intersection-observer";
import { formatTotalPercentage } from "../../../helpers/coa-report";
import { SHOP_PDP_MODAL_ENABLED } from "../../../helpers/feature-flags";
import { formatPrice } from "../../../helpers/price";
import { getDefaultProductItemVariant } from "../../../helpers/product";
import { getProductDetailsPath } from "../../../helpers/url";
import { PodHistoryType } from "../../../hooks/use-activated-pod-history";
import { analyticsTrackProductClick } from "../../../services/analytics";
import { PriceType, ProductListItemType } from "../../../types/airgraft";
import ProductDetailsModal from "../ProductDetailsModal";
import ProductExpressLabel, {
  ProductExpressLabelType,
} from "./ProductExpressLabel";
import {
  ActionButton,
  CustomImgixImage,
  InfoTypography,
  PriceTypography,
  SecondaryInfoTypography,
  SmallPodKeyInfo,
  TitleTypography,
  WrapperLink,
  WrapperSkeleton,
} from "./styles";

type ProductCardVariantType = "SHOP" | "MY_PODS" | "SKELETON";

type Props = {
  variant: ProductCardVariantType;
  product?: ProductListItemType;
  shopAvailabilityLabel?: ProductExpressLabelType;
  price?: number; // Custom shop price, else fallback to DatoCMS product variant price
  podHistory?: PodHistoryType;
};

function ProductCard({
  variant,
  product,
  shopAvailabilityLabel,
  price,
  podHistory,
}: Props) {
  const theme = useTheme();

  if (!product && variant !== "SKELETON") {
    throw new Error(
      "You must set <ProductCard> variant to 'SKELETON' if you do not pass any product object."
    );
  }

  if (variant === "MY_PODS" && !podHistory) {
    throw new Error(
      "You must provide <ProductCard> a podHistory prop when using variant='MY_POD'."
    );
  }

  const productVariant = product ? getDefaultProductItemVariant(product) : null;
  if (product && !productVariant) {
    return null;
  }

  const variantDetail = productVariant?.variantDetail;
  const coaReport = productVariant?.variantDetail?.coaReport;
  const thcPercent = coaReport?.thcTotalPercent;
  const cbdPercent = coaReport?.cbdTotalPercent;
  const productCardPrice: PriceType = {
    amount: price !== undefined ? price : productVariant?.price,
    currencyCode: "USD",
  };

  const enableProductDetailsModal =
    SHOP_PDP_MODAL_ENABLED && !!product && variant === "SHOP";

  const [isProductDetailsModalOpen, setProductDetailsModalOpen] =
    useState(false);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      analyticsTrackProductClick(product);
      if (enableProductDetailsModal) {
        event.preventDefault();
        setProductDetailsModalOpen(true);
      }
    },
    [product, enableProductDetailsModal]
  );

  const [inViewRef, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  if (!inView || variant === "SKELETON") {
    return (
      <WrapperSkeleton
        className="product-card"
        data-testid="product-card"
        ref={inViewRef}
      >
        <Box width="100%" paddingTop="100%" bgcolor="grey.300" />
      </WrapperSkeleton>
    );
  }

  return (
    <>
      {variant === "MY_PODS" && (
        <Helmet>
          <link
            href="https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@500&display=swap"
            rel="stylesheet"
          />
        </Helmet>
      )}

      <WrapperLink
        // @ts-ignore
        component={variant === "MY_PODS" ? "div" : undefined}
        to={
          product
            ? getProductDetailsPath(product?.productType, product.slug)
            : ""
        }
        title={`${productVariant?.title}${
          productVariant?.brand ? ` - ${productVariant?.brand.name}` : ""
        }`}
        data-testid="product-card"
        onClick={handleClick}
        className="product-card"
      >
        {/* Product image */}
        <CustomImgixImage
          src={productVariant?.featuredImage.url}
          width={424}
          height={424}
          imgixParams={{
            ar: "1",
            fit: "crop",
            auto: "format,compress",
          }}
          htmlAttributes={{
            draggable: false,
            loading: "lazy",
          }}
        />

        {/* Bottom of the card */}
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          alignSelf="stretch"
          paddingTop={{ xs: 1, sm: 1 }}
          paddingBottom={variant === "MY_PODS" ? 0 : { xs: 1, sm: 2 }}
          borderBottom={`1px solid ${theme.palette.grey[400]}`}
          borderLeft={`1px solid ${theme.palette.grey[400]}`}
          borderRight={`1px solid ${theme.palette.grey[400]}`}
          borderRadius="0 0 3px 3px"
        >
          {/* Title & Brand */}
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
            marginBottom={{ xs: 1, sm: 1 }}
            paddingX={{ xs: 1.5, sm: 2.5 }}
          >
            <TitleTypography
              variant="h6"
              style={{
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                maxWidth: "100%",
              }}
            >
              {productVariant?.title}
            </TitleTypography>
            {productVariant?.brand && (
              <Box marginTop={{ xs: 0.25, sm: 0.5 }}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    maxWidth: "100%",
                  }}
                >
                  {productVariant?.brand.name}
                </Typography>
              </Box>
            )}
          </Box>

          {/* First row: Product specifications, THC/CBD */}
          <Box
            flex={1}
            width="100%"
            borderTop={`1px solid ${theme.palette.grey[300]}`}
            pt={{ xs: 0.75, sm: 1.5 }}
            pb={{ xs: 1, sm: 1.5 }}
            paddingX={{ xs: 1.5, sm: 2.5 }}
            display="flex"
            alignItems="center"
            flexDirection={{ xs: "row", sm: "row" }}
            justifyContent={{
              xs: product?.productType !== "oil" ? "center" : "space-between",
              sm: "space-between",
            }}
            textAlign={{
              xs: product?.productType !== "oil" ? "center" : "left",
              sm: "left",
            }}
          >
            {/* Left info */}
            <Box>
              {/* Oil Strain */}
              {variantDetail?.plantSpecies && (
                <InfoTypography>{variantDetail?.plantSpecies}</InfoTypography>
              )}

              {/* Oil extraction type */}
              {variantDetail?.oilType && (
                <InfoTypography>{variantDetail.oilType}</InfoTypography>
              )}

              {/* Device left info */}
              {product?.productType !== "oil" && (
                <InfoTypography>
                  {"Works with"}
                  <br />
                  {"Airgraft 2 Pods"}
                </InfoTypography>
              )}
            </Box>

            {/* Right Info */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-end"
              justifyContent="flex-end"
              height="100%"
            >
              {product?.productType === "oil" && (
                <>
                  {/* Oil THC/CBD percentage (Desktop version) */}
                  {(variantDetail?.coaReport?.thcTotalPercent ||
                    variantDetail?.coaReport?.cbdTotalPercent) && (
                    <Box
                      display={{ xs: "none", sm: "flex" }}
                      justifyContent="flex-end"
                    >
                      {thcPercent !== undefined && (
                        <SecondaryInfoTypography component="span">
                          {`THC: ${formatTotalPercentage(thcPercent)}`}
                        </SecondaryInfoTypography>
                      )}
                      {cbdPercent !== undefined && (
                        <SecondaryInfoTypography component="span">
                          {`, CBD: ${formatTotalPercentage(cbdPercent)}`}
                        </SecondaryInfoTypography>
                      )}
                    </Box>
                  )}

                  {/* Oil THC/CBD percentage (Mobile version) */}
                  {(variantDetail?.coaReport?.thcTotalPercent ||
                    variantDetail?.coaReport?.cbdTotalPercent) && (
                    <Box
                      display={{ xs: "flex", sm: "none" }}
                      flexDirection="column"
                      alignItems="flex-end"
                    >
                      {thcPercent !== undefined && (
                        <SecondaryInfoTypography>
                          {`THC: ${formatTotalPercentage(thcPercent)}`}
                        </SecondaryInfoTypography>
                      )}
                      {cbdPercent !== undefined && (
                        <SecondaryInfoTypography>
                          {`CBD: ${formatTotalPercentage(cbdPercent)}`}
                        </SecondaryInfoTypography>
                      )}
                    </Box>
                  )}

                  {/* Oil aromas (Desktop only) */}
                  {variantDetail?.aromas && variantDetail?.aromas.length > 0 && (
                    <Hidden xsDown implementation="css">
                      <SecondaryInfoTypography component="span">
                        {variantDetail?.aromas
                          .slice(0, 3)
                          .map((a) => a.title)
                          .join(", ")}
                      </SecondaryInfoTypography>
                    </Hidden>
                  )}
                </>
              )}

              {/* Device Right info (Desktop only) */}
              {product?.productType !== "oil" && (
                <Box
                  display={{ xs: "none", sm: "flex" }}
                  justifyContent="flex-end"
                >
                  <InfoTypography color="textSecondary">
                    {"USB-C Charger"}
                  </InfoTypography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Second row (Shop variant): AGX/In-store & Price/Grams */}
          {variant === "SHOP" && (
            <Box
              width="100%"
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              paddingTop={{ xs: 0, sm: 1.5 }}
              paddingX={{ xs: 0, sm: 2.5 }}
              key={
                shopAvailabilityLabel ? "w / express-label" : "no express-label"
              } // Prevent css borderTop not updating when availabilityLabel changes
              borderTop={{
                xs: !!shopAvailabilityLabel
                  ? "0px"
                  : `1px solid ${theme.palette.grey[300]}`,
                sm: `1px solid ${theme.palette.grey[300]}`,
              }}
            >
              {/* Product label */}
              {shopAvailabilityLabel && (
                <ProductExpressLabel type={shopAvailabilityLabel} />
              )}

              {/* Price + Weight (Mobile version) */}
              <Box
                width="100%"
                display={{ xs: "flex", sm: "none" }}
                justifyContent={
                  !coaReport?.weightText ? "center" : "space-between"
                }
                paddingX={1.5}
                paddingTop={0.75}
              >
                {productCardPrice && (
                  <PriceTypography component="span">
                    {formatPrice(productCardPrice, { hideCurrencyCode: true })}
                  </PriceTypography>
                )}
                {coaReport?.weightText && (
                  <Typography variant="body1">
                    {coaReport?.weightText}
                  </Typography>
                )}
              </Box>

              {/* Price + Weight (Desktop version) */}
              <Box display={{ xs: "none", sm: "block" }}>
                {productCardPrice && (
                  <PriceTypography component="span">
                    {formatPrice(productCardPrice, { hideCurrencyCode: true })}
                  </PriceTypography>
                )}
                {coaReport?.weightText && (
                  <Typography variant="body1" component="span">
                    {" /"}
                    {coaReport?.weightText}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Second row (My pods variant): Pod key & unlocked date */}
          {variant === "MY_PODS" && podHistory && (
            <Box
              width="100%"
              display="flex"
              flexDirection={{ xs: "row", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              paddingY={{ xs: 1, sm: 1.5 }}
              borderTop={`1px solid ${theme.palette.grey[300]}`}
              paddingX={{ xs: 1.5, sm: 2.5 }}
            >
              {podHistory.podKey && (
                <SmallPodKeyInfo podKey={podHistory.podKey} />
              )}
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="flex-end"
                textAlign="right"
              >
                <InfoTypography>Unlocked on</InfoTypography>
                <InfoTypography>
                  {moment(podHistory.createdDate).format("MMM Do YYYY")}
                </InfoTypography>
              </Box>
            </Box>
          )}

          {/* Third row (My pods variant): Leave a review or Pod rating + Shop link */}
          {variant === "MY_PODS" && (
            <Box
              width="100%"
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              justifyContent="center"
              alignItems="center"
            >
              {podHistory.userRating?.rating && (
                <ActionButton to={`/rating/${podHistory.podKey}`}>
                  <Rating
                    name="rating"
                    value={podHistory.userRating.rating}
                    readOnly
                    onClick={() => navigate(`/`)}
                  />
                </ActionButton>
              )}
              {!podHistory.userRating?.rating && (
                <ActionButton to={`/rating/${podHistory.podKey}`}>
                  Leave a review
                </ActionButton>
              )}
              <ActionButton
                to={getProductDetailsPath(product.productType, product.slug)}
              >
                Buy again
              </ActionButton>
            </Box>
          )}
        </Box>

        {/* Bottom AGX border when product is available for delivery */}
        {shopAvailabilityLabel === "AGX" && (
          <Box
            bgcolor={theme.palette.expressDelivery}
            width="100%"
            height="4px"
            position="absolute"
            left={0}
            bottom={0}
          />
        )}
      </WrapperLink>

      {/* Product details modal */}
      {enableProductDetailsModal && (
        <ProductDetailsModal
          open={isProductDetailsModalOpen}
          onClose={() => setProductDetailsModalOpen(false)}
          productSelector={{
            id: product.originalId,
          }}
        />
      )}
    </>
  );
}

export default React.memo(ProductCard, deepEqual);
